<template>
  <div class="flex flex-col bg-white rounded-xl w-full">
    <woot-modal-header-v2
      :header-title="$t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.TITLE')"
      :header-content="$t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.DESCRIPTION')"
    >
      <template #titleIcon>
        <v2-icon icon="add" class="text-gray-500" />
      </template>
    </woot-modal-header-v2>

    <div class="flex flex-col gap-3 px-6 py-4">
      <dropdown-v2
        :value="selectedListId"
        :options="listOptions"
        :label="$t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.SELECT_LABEL')"
        :placeholder="$t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.SELECT_PLACEHOLDER')"
        :search-placeholder="
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.SEARCH_PLACEHOLDER')
        "
        :empty-text="
          listsLoading
            ? $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.LOADING')
            : $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.NO_RESULTS')
        "
        searchable
        @input="onSelectList"
        @close="onListPickerClose"
        @input.native="onListSearchInput"
        @scroll.native.capture="onListScroll"
      />

      <div class="flex items-center gap-3">
        <div class="flex-1 h-px bg-gray-200" />
        <span class="text-sm leading-5 text-gray-300">{{
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.OR')
        }}</span>
        <div class="flex-1 h-px bg-gray-200" />
      </div>

      <button-v2
        variant="outline"
        size="lg"
        block
        @click="showCreateList = true"
      >
        <template #icon>
          <v2-icon icon="list_add" :size="24" />
        </template>
        {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATE_NEW_LIST') }}
      </button-v2>

      <div
        v-if="showCreateList"
        class="flex flex-col gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
      >
        <label class="text-sm text-slate-700">{{
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.LIST_NAME')
        }}</label>
        <input
          v-model.trim="newListName"
          type="text"
          :placeholder="
            $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.LIST_NAME_PLACEHOLDER')
          "
          class="h-11 w-full px-3 rounded-lg border border-slate-300 bg-white text-base text-slate-900 outline-none focus:ring-2 focus:ring-brand transition-colors"
          @keydown.enter.prevent="createAndAdd"
        />
        <div class="flex items-center justify-end gap-2 mt-1">
          <button
            type="button"
            class="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded transition-colors"
            @click="
              showCreateList = false;
              newListName = '';
            "
          >
            {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CANCEL') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded transition-colors"
            :disabled="!newListName || uiFlags.isCreating || !canCreateAndAdd"
            :title="canCreateAndAdd ? null : disabledActionHint"
            :class="
              newListName && canCreateAndAdd
                ? 'bg-brand text-white hover:bg-brand-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            "
            @click="createAndAdd"
          >
            {{
              uiFlags.isCreating
                ? $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATING')
                : $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATE_AND_ADD')
            }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-end gap-3 pt-4 pb-6 px-6 border-t border-slate-100"
      style="box-shadow: 0px -1px 4px 0px rgba(202, 202, 202, 0.16)"
    >
      <button-v2
        variant="naked"
        color="neutral"
        size="lg"
        class="cancel-filter-btn"
        @click="$emit('close')"
      >
        {{ $t('CONTACTS_FILTER.CANCEL_BUTTON_LABEL') }}
      </button-v2>
      <button-v2
        variant="filled"
        size="lg"
        :disabled="
          !selectedListId || uiFlags.isAddingMember || !canManageListMembers
        "
        :title="canManageListMembers ? null : disabledActionHint"
        class="apply-filter-btn"
        @click="addToList"
      >
        {{ $t('CONTACTS_FILTER.ADD_BUTTON_LABEL') }}
      </button-v2>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import listPermissionsMixin from 'v2/helpers/listPermissions';
import {
  addMemberErrorMessage,
  skippedMemberMessage,
} from 'v2/helpers/listErrors';
import V2Icon from 'v2/components/Icon.vue';
import ListsAPI from 'dashboard/api/lists';
import { debounce } from '@chatwoot/utils';

const LISTS_PER_PAGE = 20;

export default {
  components: { V2Icon },
  mixins: [alertMixin, listPermissionsMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      selectedListId: null,
      selectedList: null,
      showCreateList: false,
      newListName: '',
      lists: [],
      listsPage: 0,
      listsHasMore: false,
      listsLoading: false,
      listSearch: '',
      listsRequestId: 0,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'lists/getUIFlags',
    }),
    listOptions() {
      const lists = [...this.lists];
      if (
        this.selectedList &&
        !lists.some(list => list.id === this.selectedList.id)
      ) {
        lists.unshift(this.selectedList);
      }
      return lists.map(list => ({ value: list.id, label: list.name }));
    },
    canCreateAndAdd() {
      return this.canCreateList && this.canManageListMembers;
    },
  },
  created() {
    this.searchListsDebounced = debounce(() => this.fetchLists(1), 300);
  },
  mounted() {
    this.fetchLists(1);
  },
  methods: {
    async fetchLists(page) {
      this.listsRequestId += 1;
      const requestId = this.listsRequestId;
      this.listsLoading = true;
      try {
        const params = { per_page: LISTS_PER_PAGE };
        if (this.listSearch) params.search = this.listSearch;
        const {
          data: { payload = [], meta = {} },
        } = await ListsAPI.get(page, params);
        if (requestId !== this.listsRequestId) return;
        const userLists = payload.filter(list => !list.system_list);
        this.lists = page === 1 ? userLists : [...this.lists, ...userLists];
        this.listsPage = page;
        this.listsHasMore = page * LISTS_PER_PAGE < (meta.count || 0);
      } catch (error) {
        if (requestId === this.listsRequestId) this.listsHasMore = false;
      } finally {
        if (requestId === this.listsRequestId) this.listsLoading = false;
      }
    },
    onSelectList(listId) {
      this.selectedListId = listId;
      this.selectedList =
        this.lists.find(list => list.id === listId) || this.selectedList;
    },
    onListSearchInput(event) {
      this.listSearch = event.target.value.trim();
      this.listsHasMore = false;
      this.searchListsDebounced();
    },
    onListScroll(event) {
      const panel = event.target;
      if (!this.listsHasMore || this.listsLoading || !panel.scrollHeight) {
        return;
      }
      if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 48) {
        this.fetchLists(this.listsPage + 1);
      }
    },
    onListPickerClose() {
      if (!this.listSearch) return;
      this.listSearch = '';
      this.fetchLists(1);
    },
    async addToList() {
      if (!this.selectedListId || !this.canManageListMembers) return;
      try {
        const result = await this.$store.dispatch('lists/addMember', {
          listId: this.selectedListId,
          contactId: this.contactId,
        });
        this.notifyAddResult(result);
        this.$emit('close');
      } catch (error) {
        this.showAlert(this.addErrorMessage(error));
      }
    },
    notifyAddResult(result) {
      const skipped = skippedMemberMessage(result, key => this.$t(key));
      this.showAlert(skipped || this.$t('LISTS_PAGE.ADD_CONTACT.SUCCESS'));
    },
    addErrorMessage(error) {
      return addMemberErrorMessage(
        error,
        key => this.$t(key),
        this.$t('LISTS_PAGE.ADD_CONTACT.ERROR')
      );
    },
    async createAndAdd() {
      if (!this.newListName || !this.canCreateAndAdd) return;
      try {
        const list = await this.$store.dispatch('lists/create', {
          name: this.newListName,
        });
        const result = await this.$store.dispatch('lists/addMember', {
          listId: list.id,
          contactId: this.contactId,
        });
        this.notifyAddResult(result);
        this.$emit('close');
      } catch (error) {
        this.showAlert(this.addErrorMessage(error));
      }
    },
  },
};
</script>
